import type { ScreenDesignInstance } from "./designer-context";

/**
 * Reload action definitions from the backend into the frontend cache
 * (`sd.actionDefinitions`), then wait for React state to apply.
 *
 * `loadActionDefinitions()` finishes its HTTP calls but writes through
 * `setState`, which is asynchronous — a caller that reads the catalog straight
 * after the await would still see the pre-reload value. The brief delay lets
 * the setState callback land.
 *
 * KOA-17363 — the MCP path passes `skipEagerTableFetch=true` so the reload does
 * NOT trigger the multi-MB eager schema fetch that body-timed-out on tenants
 * with large datasource schemas. Designer flows keep the eager behaviour by
 * calling `loadActionDefinitions()` directly.
 */
export async function reloadActionDefinitions(sd: ScreenDesignInstance): Promise<void> {
  if ("projectProvider" in sd && sd.projectProvider?.loadActionDefinitions) {
    await sd.projectProvider.loadActionDefinitions(undefined, true);
    await new Promise((resolve) => setTimeout(resolve, 300));
  }
}

/**
 * KOA-18136 C2 — one-shot self-heal for a STALE browser action catalog.
 *
 * `sd.actionDefinitions` is populated at PROJECT LOAD. Anything that resolves
 * an action BY NAME reads it, so an action created earlier in the same turn
 * (server side, by the Backend Architect) is invisible until a reload runs —
 * and the miss is indistinguishable from "this action does not exist". Live
 * smoke 21: four freshly created SQL actions could not be attached as workflow
 * STEP_ACTIONs, the model double-checked via `query_datasource_actions` (which
 * reads the SAME stale catalog, so it agreed), and deleted the four system
 * steps that needed them — leaving a process that persists nothing.
 *
 * The screen-action tools have carried this heal since KOA-17394 (add_to_event
 * retries once after a reload); the workflow surface did not. This is the same
 * contract, expressed against the RESOLVER instead of an error-message regex:
 * ask whether the name resolves, reload once per script if it does not, and let
 * the caller proceed either way (a genuinely non-existent action still fails
 * closed with the resolver's own message + closest matches).
 *
 * Once per script, not per command: a script attaching four unknown actions
 * must pay for at most one reload, and a second reload cannot help — the
 * catalog it would fetch is the one just fetched.
 */
export interface ActionCatalogHealerDeps {
  /** Whether the browser catalog can resolve this action name RIGHT NOW. */
  readonly canResolve: (actionName: string) => boolean;
  /** Reload the action definitions from the backend into the browser catalog. */
  readonly reload: () => Promise<void>;
}

export interface ActionCatalogHealer {
  /**
   * Reload the catalog when `actionName` does not resolve and no reload has run
   * yet for this script. Resolves to whether a reload actually ran.
   */
  readonly ensureResolvable: (actionName: string | undefined) => Promise<boolean>;
  /** Whether this healer has already spent its one reload. */
  readonly didReload: () => boolean;
}

export function createActionCatalogHealer(deps: ActionCatalogHealerDeps): ActionCatalogHealer {
  let reloaded: boolean = false;

  return {
    didReload: (): boolean => reloaded,
    ensureResolvable: async (actionName: string | undefined): Promise<boolean> => {
      const name: string = (actionName ?? "").trim();
      if (name.length === 0 || reloaded) return false;
      if (deps.canResolve(name)) return false;
      reloaded = true;
      try {
        await deps.reload();
      } catch (error: unknown) {
        // A failed reload must not break the attach — the resolver still runs
        // and fails closed with its own message.
        console.warn(
          `[workflow-action-catalog] reload failed while resolving "${name}": ` +
            (error instanceof Error ? error.message : String(error))
        );
      }
      return true;
    }
  };
}
